/**
 * Normalize Key Safety Notes inner markdown before rendering.
 *
 * Models often append `⚠` caveats on the same line as the last cited bullet
 * (especially right after a cite-badge). This splits them onto separate lines.
 * Caveat lines are returned as plain `⚠ …` text (not markdown list items).
 */
export function formatKeySafetyNotesMarkdown(markdown: string): string {
  if (!markdown) return '';

  const lines: string[] = [];

  for (const physicalLine of markdown.split('\n')) {
    if (!physicalLine.trim()) continue;

    const trimmedStart = physicalLine.trimStart();

    // Model already formatted as a list item — strip the marker, keep ⚠ line.
    if (/^-\s*⚠/.test(trimmedStart)) {
      lines.push(trimmedStart.replace(/^-\s*/, ''));
      continue;
    }

    if (!physicalLine.includes('⚠')) {
      lines.push(physicalLine.trimEnd());
      continue;
    }

    // Split cited prose from inline caveat(s) on the same physical line.
    for (const segment of physicalLine.split(/(?=⚠)/)) {
      const trimmed = segment.trimEnd();
      // Lone "-" left when splitting "- ⚠ …" — discard.
      if (!trimmed || /^-\s*$/.test(trimmed)) continue;
      lines.push(trimmed);
    }
  }

  return lines.join('\n');
}

/** Split cited safety bullets from conditional ⚠ caveat lines. */
export function partitionKeySafetyNotesContent(markdown: string): {
  citedMarkdown: string;
  caveatLines: string[];
} {
  const cited: string[] = [];
  const caveats: string[] = [];

  for (const line of formatKeySafetyNotesMarkdown(markdown).split('\n')) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    if (trimmed.startsWith('⚠')) {
      caveats.push(trimmed);
    } else {
      cited.push(line.trimEnd());
    }
  }

  return { citedMarkdown: cited.join('\n'), caveatLines: caveats };
}

/** Strip the leading warning glyph so caveats can render as plain bullets. */
export function stripCaveatWarningPrefix(line: string): string {
  return line.replace(/^⚠\s*/, '').trim();
}

/**
 * Pictographic characters a model may emit inside safety-note prose — emoji
 * planes, the Miscellaneous Symbols / Dingbats blocks (⚠ ✅ ❌ ⚡), the
 * symbols-and-arrows block, plus the joiners and variation selectors that glue
 * emoji sequences together.
 *
 * Deliberately excludes the Latin-1 and Mathematical Operators blocks so
 * clinical notation (`≥`, `±`, `µg`, `°C`, `→`) survives untouched.
 */
const DECORATIVE_ICON_PATTERN =
  /[\u2139\u2600-\u27BF\u2B00-\u2BFF\u{1F000}-\u{1FAFF}\uFE0E\uFE0F\u200D]/gu;

/** A line left holding only a list marker (and whitespace) after stripping. */
const EMPTY_LIST_ITEM_PATTERN = /^\s*(?:[-*+]|\d{1,9}[.)])?\s*$/;

/**
 * Remove decorative icons from safety-note content.
 *
 * The block renders bullets only — the backend prompt asks for plain prose but
 * does not guarantee it, and an emitted `✅` / `⚠` lands in the middle of the
 * bullet text where no component styling can reach it. Stripping here makes the
 * "bullets, no icons" rule hold whatever the model returns.
 *
 * Only lines that held a glyph are touched: whitespace orphaned by the removal
 * is collapsed, and a line reduced to a bare list marker is dropped so the list
 * never renders an empty bullet. Every other line passes through byte-for-byte —
 * trailing double spaces are a markdown hard break.
 */
export function stripDecorativeIcons(text: string): string {
  if (!text) return '';

  return text
    .split('\n')
    .flatMap((line) => {
      // Indentation is captured before stripping so a nested bullet keeps its
      // level even when the glyph it led with is removed.
      const [, indent = '', rest = ''] = /^([ \t]*)([\s\S]*)$/.exec(line) ?? [];
      const stripped = rest.replace(DECORATIVE_ICON_PATTERN, '');
      if (stripped === rest) return [line];
      const body = stripped.replace(/[ \t]{2,}/g, ' ').trim();
      return EMPTY_LIST_ITEM_PATTERN.test(body) ? [] : [`${indent}${body}`];
    })
    .join('\n');
}

/** A top-level `- ` / `* ` / `+ ` / `1. ` / `1) ` list marker. */
const TOP_LEVEL_LIST_ITEM_PATTERN = /^(?:[-*+]|\d{1,9}[.)])\s+/;

/**
 * How many items the cited markdown will render as.
 *
 * Used to decide whether the block shows bullets at all: one item is not a
 * list, and a lone disc under the header reads as a stray mark rather than as
 * enumeration. Indented lines are continuations or nested detail, so they don't
 * add to the count; unmarked prose with no markers at all is one paragraph.
 */
export function countTopLevelListItems(markdown: string): number {
  let items = 0;
  let hasUnmarkedProse = false;

  for (const line of markdown.split('\n')) {
    if (!line.trim()) continue;
    if (/^[ \t]/.test(line)) continue;
    if (TOP_LEVEL_LIST_ITEM_PATTERN.test(line)) items += 1;
    else hasUnmarkedProse = true;
  }

  return items === 0 && hasUnmarkedProse ? 1 : items;
}

/**
 * Escape a leading block-level Markdown trigger so a caveat renders as inline
 * text beside its triangle marker, never as a list / heading / blockquote /
 * thematic-break block.
 *
 * Caveats are single inline sentences. Since EVI-1803 they flow through
 * Streamdown (for `<cite-badge>` support), which also parses block syntax — so
 * a model-emitted `⚠ 1. …`, `⚠ - …`, `⚠ > …`, or `⚠ # …` would otherwise
 * reflow the hanging-indent layout into a nested block. Only the leading
 * trigger is escaped; inline syntax (bold, links, cite-badge) is preserved.
 *
 * Each pattern matches Markdown's actual block conditions (e.g. a heading /
 * bullet needs whitespace after the marker), so legitimate prose like
 * `5-10 mg` or `#1 priority` is left untouched.
 */
export function escapeLeadingBlockMarkdown(text: string): string {
  return (
    text
      // ATX heading: `#`…`######` followed by whitespace.
      .replace(/^(#{1,6})(\s)/, '\\$1$2')
      // Blockquote: leading `>`.
      .replace(/^(>)/, '\\$1')
      // Bullet list item: `-` / `*` / `+` followed by whitespace.
      .replace(/^([-*+])(\s)/, '\\$1$2')
      // Ordered list item: `1.` / `1)` followed by whitespace.
      .replace(/^(\d{1,9})([.)])(\s)/, '$1\\$2$3')
      // Thematic break: a line of only `-`/`*`/`_` (3+, optional spaces).
      .replace(/^([-*_])((?:\s*[-*_]){2,}\s*)$/, '\\$1$2')
  );
}
